import BaseCachedDataService from '@/core/services/cachedData/BaseCachedDataService'
import { IDashboard, IExtendedDashboard } from '@/types/dashboard'
import { DashboardApi } from '@/api/dashboardApi'
import { ActiveDashboard } from '@/composables/globalData/useDashoardsData'

const MAX_EXTENDED_DASHBOARDS = 10  // Maximum number of extended dashboards to keep in cache

class DashboardCachedDataService extends BaseCachedDataService<Array<IDashboard | IExtendedDashboard>> {
    constructor () {
        super([])
    }

    async loadData () {
        // Step 1: Fetch all dashboards from API (basic data without widgets)
        const dashboards: Array<IDashboard | IExtendedDashboard> = await DashboardApi.getAll()
        const activeDashboardIndexInLoadedDashboards = dashboards.findIndex(dashboard => dashboard.DashboardID === ActiveDashboard.value)

        // Step 2: Create map of currently cached extended dashboards to preserve them
        // This ensures that dashboards previously loaded with full widget data stay cached
        const currentExtendedDashboards = this.data.filter(d => 'extended' in d)
        const extendedDashboardMap = new Map(
            currentExtendedDashboards.map(d => [ d.DashboardID, d ])
        )

        // Step 3: Load active dashboard with full widget data
        // The active dashboard always gets fresh data and stays in cache
        if (activeDashboardIndexInLoadedDashboards !== -1) {
            const activeDashboardID = dashboards[activeDashboardIndexInLoadedDashboards].DashboardID
            
            if (activeDashboardID) {
                // Fetch full dashboard data including all widget groups
                const extendedDashboardData = await DashboardApi.find(activeDashboardID)

                if (extendedDashboardData) {
                    dashboards[activeDashboardIndexInLoadedDashboards] = {
                        ...extendedDashboardData,
                        extended: true,
                        lastAccessedAt: Date.now()  // Mark access time for LRU eviction
                    }
                }
            }
        }

        // Step 4: Restore previously extended dashboards from cache
        // Combines fresh metadata from getAll() with cached widget data
        dashboards.forEach((dashboard, index) => {
            if (extendedDashboardMap.has(dashboard.DashboardID) && !('extended' in dashboard)) {
                const cachedExtendedDashboard = extendedDashboardMap.get(dashboard.DashboardID) as IExtendedDashboard
                dashboards[index] = {
                    ...dashboard,  // Fresh metadata (title, config, permissions)
                    WidgetGroupList: cachedExtendedDashboard.WidgetGroupList,  // Cached widgets
                    extended: true,
                    lastAccessedAt: cachedExtendedDashboard.lastAccessedAt || Date.now()  // Preserve LRU timestamp
                }
            }
        })

        // Step 5: LRU Cache Management - Evict oldest dashboards if cache limit exceeded
        const extendedDashboards = dashboards.filter((d): d is IExtendedDashboard => 'extended' in d)
        
        if (extendedDashboards.length > MAX_EXTENDED_DASHBOARDS) {
            // Sort by lastAccessedAt (oldest first), but protect the active dashboard from eviction
            const sortedByAge = [ ...extendedDashboards ]
                .filter(d => d.DashboardID !== ActiveDashboard.value)  // Active dashboard is never evicted
                .sort((a, b) => (a.lastAccessedAt || 0) - (b.lastAccessedAt || 0))
            
            // Calculate how many dashboards need to be evicted
            const dashboardsToRemoveCount = Math.max(0, extendedDashboards.length - MAX_EXTENDED_DASHBOARDS)
            const dashboardIDsToRemove = new Set(
                sortedByAge.slice(0, dashboardsToRemoveCount).map(d => d.DashboardID)
            )
            
            // Convert extended dashboards back to basic dashboards (remove widget data)
            for (let i = 0; i < dashboards.length; i++) {
                const dashboard = dashboards[i]
                if ('extended' in dashboard && dashboardIDsToRemove.has(dashboard.DashboardID)) {
                    // Remove extended flag and widget data to free memory
                    const { extended, lastAccessedAt, ...basicDashboard } = dashboard
                    dashboards[i] = {
                        ...basicDashboard,
                        WidgetGroupList: []  // Clear widgets to reduce memory usage
                    }
                }
            }
        }

        return this.data = dashboards || []
    }
}

export default new DashboardCachedDataService()
